import { default as React } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { DocsIcon } from '@backstage/core-components';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { WorkloadClient } from '@internal/workload-api-helper';
import {
  Card,
  CardActions,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import useAsync from 'react-use/lib/useAsync';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import {
  Account,
  AllowedRepositories,
  Features,
  GetMessageObjectEnumResponse,
  GetMessageObjectInterface,
} from '../types';
import { isResponseCodeAcceptable, isValueDefined } from '../utils/checks';
import { updateResource } from '../utils/patching';
import { parseWorkloadObjectValues } from '../utils/workload';
import DeploymentRoleEditIcon from './DeploymentRoleEditIcon';
import { DeploymentRoleRepositoriesTable } from './DeploymentRoleRepositoriesTable';
import { useAccountId } from '../hooks/useAccountId';

const getMessage = (
  options?: GetMessageObjectInterface,
): GetMessageObjectEnumResponse => {
  return Object.freeze({
    REFRESHING: 'Refreshing to show updates. This may take a couple minutes.',
    FAILURE: `Failed to ${options?.failureToDo}. Please try again in a few minutes or Contact an admin for further instruction by completing a ServiceNow ticket here: https://principal.service-now.com/esc?id=sc_cat_item&sys_id=909dfec91b8be514fb470ed5604bcb3d`,
    SUCCESS: options?.successMessage ?? '',
  });
};

interface RepoFormFieldName {
  oidcCdkRoleRepo: string;
  pfg_owner: string;
}

const fetchWorkloadData = async (
  workloadClient: WorkloadClient,
  accountId: string,
) => {
  return await workloadClient.apiFetch(`workloads/${accountId}`);
};

export function DeploymentRoleCard() {
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const identity = useApi(identityApiRef);
  const config = useApi(configApiRef);

  const [workloadData, setWorkloadData] = useState<Account>();
  const [workloadDataAPIResponse, setWorkloadDataAPIResponse] =
    useState<Response>();
  const [loadingRequiredEditData, setLoadingRequiredEditData] =
    useState<boolean>(false);

  const { entity } = useEntity();
  const entityFeatures = entity?.spec?.features as Features;
  const [savedRepositories, setSavedRepositories] =
    useState<AllowedRepositories[]>();
  const features = useMemo<Features>(
    () =>
      savedRepositories && entityFeatures?.oidc
        ? {
            ...entityFeatures,
            oidc: {
              ...entityFeatures.oidc,
              allowed: {
                ...entityFeatures.oidc.allowed,
                repositories: savedRepositories,
              },
            },
          }
        : entityFeatures,
    [entityFeatures, savedRepositories],
  );
  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);
  const accountId = useAccountId();

  useAsync(async (): Promise<Account> => {
    setLoadingRequiredEditData(true);
    const [workloadDetails] = await Promise.all([
      fetchWorkloadData(workloadClient, accountId),
    ]);

    setWorkloadDataAPIResponse(workloadDetails);

    const [details] = await Promise.all([workloadDetails.json()]);
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);

    setLoadingRequiredEditData(false);

    return details;
  }, []);

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const FormFieldEnum: RepoFormFieldName = Object.freeze({
    oidcCdkRoleRepo: 'repository',
    pfg_owner: 'pfg_owner',
  });

  const formContext = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      [FormFieldEnum.oidcCdkRoleRepo]: String(
        features?.oidc_cdk_role?.repos[0] || '',
      ),
      [FormFieldEnum.pfg_owner]: requesterEmail,
    },
  });

  const apiValidationPayload = {
    form: formContext,
    identity: identity,
    config: config,
  };

  useEffect(() => {
    formContext.setValue(FormFieldEnum.pfg_owner, requesterEmail);
  }, [requesterEmail, FormFieldEnum.pfg_owner, formContext]);

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
    setIsLoading(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleConfirm = async (
    e: React.FormEvent<HTMLFormElement>,
    repositories?: AllowedRepositories[],
  ): Promise<boolean> => {
    e.preventDefault();
    setIsLoading(true);

    let workloadObject: { [key: string]: string | undefined } = {};
    const workloadDataStatus: number | undefined =
      workloadDataAPIResponse?.status;

    const catalog_location =
      entity?.metadata?.annotations?.['backstage.io/managed-by-location'];
    const catalogLocationStringList = catalog_location?.split('/');
    if (catalogLocationStringList) {
      workloadObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return false;
    }
    const oidc =
      repositories && features.oidc
        ? {
            ...features.oidc,
            allowed: { ...features.oidc.allowed, repositories },
          }
        : features.oidc;
    let payload = {};
    payload = { oidc };
    let saved = false;

    if (
      isResponseCodeAcceptable(workloadDataStatus) &&
      isValueDefined(workloadObject?.workload)
    ) {
      saved = await updateResource({
        workloadClient,
        workloadObject,
        payload,
        stopLoadingAndCloseModal,
        alertApi,
        killProcessAndReportError,
      });
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({ failureToDo: 'retrieve workload' }).FAILURE,
        severity: SeverityEnum.WARNING,
      });
    }
    if (saved && repositories) {
      setSavedRepositories(repositories);
    }
    await catalogApi.refreshEntity(stringifyEntityRef(entity));
    stopLoadingAndCloseModal();
    return saved;
  };

  return (
    <Card>
      <CardHeader
        title="Foundation Managed OIDC"
        titleTypographyProps={{ component: 'h2' }}
        action={
          <CardActions>
            <Tooltip title="Learn more about GitHub deployments in AWS">
              <IconButton
                href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/what-comes-with-my-aws-account/#github-oidc-deployment-role"
                target="_blank"
              >
                <DocsIcon />
              </IconButton>
            </Tooltip>
            <DeploymentRoleEditIcon
              apiValidationPayload={apiValidationPayload}
              features={features}
              handleClickOpen={handleClickOpen}
              handleClose={handleClose}
              handleConfirm={handleConfirm} // Pass handleConfirm as a prop
              isLoading={isLoading}
              isLoadingEdit={loadingRequiredEditData}
              isModalOpen={isModalOpen}
              requesterEmail={requesterEmail}
              workloadData={workloadData}
            />
          </CardActions>
        }
      />
      <Divider />
      <DeploymentRoleRepositoriesTable
        allowedRepositories={features.oidc?.allowed.repositories || []}
      />
    </Card>
  );
}
